import { useParams, Link } from 'react-router-dom';
import SEO, {
  getWebPageSchema,
  getBreadcrumbSchema,
  getArticleSchema,
} from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

const categoryNameMap: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  dongman: '动漫',
  jilupian: '纪录片',
  yinyue: '音乐',
  youxi: '游戏',
  keji: '科技',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);

  const article = articles.find((a) => a.id === articleId);
  const video = videos.find((v) => v.id === articleId);
  const item = article || video;
  const isArticle = !!article;

  if (!item) {
    return (
      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-secondary-100 rounded-full">
            <i className="ri-error-warning-line text-secondary-400 text-2xl"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">内容未找到</h1>
          <p className="text-sm text-foreground-600 mb-6">您访问的内容可能已被移除或不存在</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const catKey = isArticle ? (article as typeof articles[0]).category : (video as typeof videos[0]).category;
  const categoryName = categoryNameMap[catKey] || catKey;

  const title = `${item.title} - 就要草视频`;
  const description = item.description || (isArticle ? (item as typeof articles[0]).excerpt : '');
  const keywords = `${item.title},就要草视频,${categoryName},在线观看`;
  const canonicalUrl = `https://jiuyaocao.com/article/${articleId}`;
  const imageUrl = isArticle
    ? (item as typeof articles[0]).thumbnail
    : (item as typeof videos[0]).thumbnail;

  return (
    <>
      <SEO
        title={title}
        description={description}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
        ogImage={imageUrl}
        ogType="article"
        jsonLd={[
          getWebPageSchema(title, description, canonicalUrl),
          getBreadcrumbSchema([
            { name: '首页', url: 'https://jiuyaocao.com/' },
            { name: categoryName, url: `https://jiuyaocao.com/category/${catKey}` },
            { name: item.title, url: canonicalUrl },
          ]),
          ...(isArticle
            ? [
                getArticleSchema({
                  title: item.title,
                  description: (item as typeof articles[0]).excerpt,
                  url: canonicalUrl,
                  imageUrl: (item as typeof articles[0]).thumbnail,
                  datePublished: (item as typeof articles[0]).date,
                  authorName: (item as typeof articles[0]).author,
                }),
              ]
            : []),
        ]}
      />

      <main>
        <div className="pt-20 pb-6 bg-background-100">
          <Breadcrumb
            items={[
              { name: '首页', url: '/' },
              { name: categoryName, url: `/category/${catKey}` },
              { name: item.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${catKey}`}
                  className="text-xs px-2.5 py-1 bg-accent-100 text-accent-700 rounded whitespace-nowrap cursor-pointer"
                >
                  {categoryName}
                </Link>
                {!isArticle && (
                  <span className="text-xs px-2.5 py-1 bg-primary-100 text-primary-700 rounded whitespace-nowrap">
                    视频
                  </span>
                )}
                {isArticle && (
                  <span className="text-xs px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded whitespace-nowrap">
                    文章
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 font-heading leading-tight">
                {item.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-secondary-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {isArticle ? (item as typeof articles[0]).author : (item as typeof videos[0]).author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {item.date}
                </span>
                {!isArticle && (
                  <>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {(item as typeof videos[0]).views >= 10000
                        ? `${((item as typeof videos[0]).views / 10000).toFixed(1)}万次播放`
                        : `${(item as typeof videos[0]).views}次播放`}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {(item as typeof videos[0]).duration}
                    </span>
                  </>
                )}
                {isArticle && (
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {(item as typeof articles[0]).readTime}
                  </span>
                )}
              </div>
            </header>

            {!isArticle && (
              <div className="relative overflow-hidden rounded-lg mb-8 aspect-video">
                <img
                  src={(item as typeof videos[0]).thumbnail}
                  alt={item.title}
                  title={`${item.title} - 就要草视频`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center cursor-pointer">
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center bg-primary-500 rounded-full hover:bg-primary-600 transition-colors">
                    <i className="ri-play-fill text-background-50 text-2xl md:text-3xl ml-1"></i>
                  </div>
                </div>
              </div>
            )}

            {isArticle && (
              <div className="relative overflow-hidden rounded-lg mb-8 aspect-video">
                <img
                  src={(item as typeof articles[0]).thumbnail}
                  alt={item.title}
                  title={`${item.title} - 就要草视频`}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed">
              {isArticle ? (
                (item as typeof articles[0]).content.split('\n').map((paragraph, i) => {
                  if (paragraph.startsWith('## ')) {
                    return (
                      <h2 key={i} className="text-xl font-bold text-foreground-900 mt-8 mb-4 font-heading">
                        {paragraph.replace('## ', '')}
                      </h2>
                    );
                  }
                  if (paragraph.trim() === '') {
                    return <br key={i} />;
                  }
                  return (
                    <p key={i} className="mb-4 text-foreground-700">
                      {paragraph}
                    </p>
                  );
                })
              ) : (
                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <h2 className="text-lg font-semibold text-foreground-900 mb-4 font-heading">
                    视频简介
                  </h2>
                  <p className="text-foreground-700 mb-4">
                    {item.description}
                  </p>
                  <div className="flex items-center gap-6 text-sm text-secondary-500">
                    <span>
                      评分：<strong className="text-accent-500 text-lg">{(item as typeof videos[0]).rating}</strong> / 10
                    </span>
                    <span>
                      播放量：<strong>{(item as typeof videos[0]).views >= 10000 ? `${((item as typeof videos[0]).views / 10000).toFixed(1)}万` : (item as typeof videos[0]).views}</strong>
                    </span>
                  </div>

                  {('tags' in item) && (
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                      {(item as typeof videos[0]).tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-secondary-100 text-secondary-700 text-sm font-medium rounded-lg hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}